import { z } from 'zod';
import { SUBSCRIPTION_STATUSES, SUBSCRIPTION_STORES } from './billing.js';
import { GYM_COACHING_ENDED_BY, GYM_MEMBER_STATUSES, GYM_STAFF_STATUSES } from './gyms.js';
import { cursorPageQuerySchema } from './pagination.js';
import { DIET_TYPES, GOAL_TYPES, PLAN_STATUSES, ADMIN_USER_STATUSES, nutritionTargetsSchema, } from './users.js';
/**
 * The admin user detail screen: one header, five tabs.
 *
 * Two rules shape every schema in this file.
 *
 * **Health data is omitted, not hidden.** Conditions, medications, allergies and
 * pregnancy status never appear in any schema here. `healthRedactionSchema`
 * carries counts so the UI can render "•••••• (2 redacted)" without the values
 * ever crossing the wire; the values themselves come only from
 * `adminUserHealthSchema`, which has its own endpoint, its own role gate and an
 * audit row. The imported design reveals by flipping local state over data it
 * already holds — that is explicitly rejected (design spec §5).
 *
 * **Nothing is invented.** Where the design draws a field the schema cannot
 * back, it is absent and `user-detail.test.ts` asserts that it stays absent.
 */
// --- Vocabularies -----------------------------------------------------------
// Lower-cased mirrors of the Prisma enums. `admin-enum-wire.ts` on the API side
// proves at compile time that these tuples and the database agree.
export const SEXES = ['male', 'female', 'other'];
export const ACTIVITY_LEVELS = ['sedentary', 'light', 'moderate', 'very_active'];
export const TARGET_DEADLINES = [
    'one_month',
    'two_months',
    'three_months',
    'six_months',
    'one_year',
];
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const WORKOUT_LOCATIONS = [
    'home_no_equipment',
    'home_basic_equipment',
    'gym',
    'yoga_pilates',
    'outdoor',
];
export const PREFERRED_TIMES = ['early_morning', 'morning', 'lunch', 'evening', 'night'];
export const EXPERIENCE_LEVELS = ['beginner', 'some', 'advanced'];
export const INJURY_AREAS = [
    'knee',
    'lower_back',
    'shoulder',
    'wrist',
    'elbow',
    'hip',
    'ankle',
    'neck',
];
export const HEALTH_CONDITIONS = ['pcos', 'diabetes_type_2', 'pre_diabetes', 'ibs_digestive'];
export const ALLERGENS = ['lactose', 'gluten', 'nuts', 'eggs', 'shellfish', 'soy'];
export const MEAL_TYPES = ['breakfast', 'mid_morning', 'lunch', 'snack', 'dinner'];
export const MEAL_LOG_SOURCES = ['manual', 'photo_scan', 'plan', 'swap'];
export const SESSION_STATUSES = ['in_progress', 'completed', 'discarded'];
export const WEIGHT_SOURCES = ['manual', 'scale', 'import'];
export const DOSE_UNITS = ['g', 'ml', 'scoop', 'capsule', 'tablet', 'serving'];
export const SUPPLEMENT_TIMINGS = ['morning', 'pre_workout', 'post_workout', 'with_meal', 'bedtime'];
export const TRAINING_PHASES = ['base', 'accumulation', 'intensification', 'deload', 'maintenance'];
export const TRAINING_RELATIONS = ['neutral', 'pre_workout', 'post_workout'];
/**
 * What the user did with a planned meal. "Not answered" is the absence of an
 * adherence record, never a member of this list.
 */
export const MEAL_ADHERENCE_STATES = ['eaten', 'partial', 'skipped', 'swapped'];
export const RETURN_DECISION_STATES = ['cleared', 'modified', 'withheld'];
/**
 * Who plans this user's meals (Prisma's `MealPlanMode`). `weekly` is every
 * account no build has switched; `none` is "eat your own food" — the weekly
 * plan carries workouts only and the coach suggests meals from what they log.
 * A gym member's meals are their trainer's whatever this says.
 */
export const MEAL_PLAN_MODES = ['weekly', 'none'];
/** Why a Premium meal plan stopped (Prisma's `MealPlanEndedBy`). */
export const MEAL_PLAN_ENDED_BY = ['user', 'expired'];
export const CHAT_ROLES = ['user', 'assistant', 'tool'];
export const CHAT_SESSION_STATUSES = ['active', 'ended'];
/**
 * `identified` sits between `pending` and `completed`: the vision model has
 * named the dishes and the cascade is still pricing them. A scan resting there
 * is working, not stuck. `discarded` is the user throwing the scan away.
 */
export const FOOD_SCAN_STATUSES = [
    'pending',
    'identified',
    'completed',
    'failed',
    'confirmed',
    'discarded',
];
/**
 * Why a whole scan failed. The first three are the photo's problem, the last
 * two ours (and retryable by the user).
 */
export const FOOD_SCAN_FAILURE_CODES = [
    'photo_unreadable',
    'no_food',
    'not_in_catalog',
    'provider_unavailable',
    'internal',
];
// --- Header aggregates ------------------------------------------------------
/**
 * The window every aggregate on this screen is measured over. Fixed rather than
 * a query parameter: two operators comparing notes must be looking at the same
 * number, and a per-user aggregate is only affordable because it is bounded.
 */
export const ADHERENCE_WINDOW_DAYS = 30;
/**
 * Adherence, defined rather than assumed.
 *
 * `daysLogged` counts *local calendar days* carrying at least one meal log, not
 * meal logs — three meals on one day is one adhering day. `workoutsPlanned`
 * counts non-rest planned workouts whose day falls inside the window, so
 * `workoutRate` is null (not zero) for a user who was never given a plan: no
 * schedule means nothing to adhere to, and 0% would read as a failure.
 */
export const adherenceSchema = z.object({
    windowDays: z.number().int(),
    /** Local dates in the window with at least one meal log. */
    daysLogged: z.number().int(),
    /** `daysLogged / windowDays`, 0–1. */
    mealLogRate: z.number(),
    workoutsCompleted: z.number().int(),
    workoutsPlanned: z.number().int(),
    /** Null when nothing was planned in the window — not zero. */
    workoutRate: z.number().nullable(),
});
/**
 * Counts, never values.
 *
 * This is the whole of what an un-revealed response says about someone's
 * health: how many facts exist, so the redaction placeholder can be honest
 * about what it is hiding. `recorded` distinguishes "nothing to show" from
 * "never asked", which the counts alone cannot.
 */
export const healthRedactionSchema = z.object({
    recorded: z.boolean(),
    conditionCount: z.number().int(),
    medicationCount: z.number().int(),
    allergyCount: z.number().int(),
});
// --- Profile tab ------------------------------------------------------------
export const userProfileSchema = z.object({
    sex: z.enum(SEXES),
    ageYears: z.number().int(),
    heightCm: z.number(),
    weightKg: z.number(),
    targetWeightKg: z.number(),
    targetDeadline: z.enum(TARGET_DEADLINES),
    activityLevel: z.enum(ACTIVITY_LEVELS),
});
export const userDietSchema = z.object({
    type: z.enum(DIET_TYPES),
    cuisines: z.array(z.string()),
    vegOnlyDays: z.array(z.enum(WEEKDAYS)),
});
/**
 * `WorkoutPreference.injuries` is deliberately absent.
 *
 * It is free text, and free text is where people write the actual medical
 * detail ("torn ACL, surgery in March"). `injuryFlags` carries the whole of the
 * operational signal — it is what exercise filtering matches on — so the nuance
 * field is left out rather than shipped un-gated beside redacted conditions.
 */
export const userWorkoutPrefsSchema = z.object({
    locations: z.array(z.enum(WORKOUT_LOCATIONS)),
    /** Kept beside `trainingDays` so list cells can show a count without joining. */
    daysPerWeek: z.number().int(),
    trainingDays: z.array(z.enum(WEEKDAYS)),
    sessionMinutes: z.number().int(),
    preferredTime: z.enum(PREFERRED_TIMES),
    experienceLevel: z.enum(EXPERIENCE_LEVELS),
    injuryFlags: z.array(z.enum(INJURY_AREAS)),
});
/**
 * A recorded decision about returning to load on an injured joint.
 *
 * `injuryFlags` above is what the exercise filter matches on, and before this
 * existed a flag excluded every movement loading that joint forever — a filter
 * that only ever got stricter. These are the rows that reverse it, and the
 * console is where they are written, because a clearance is only worth
 * anything if it is attributable to somebody.
 *
 * The whole history is returned, not just what is in force: "cleared in March,
 * withheld again in May" is the fact an operator needs, and the newest row
 * alone cannot tell it.
 */
export const returnDecisionSchema = z.object({
    id: z.string().uuid(),
    injuryArea: z.enum(INJURY_AREAS),
    decision: z.enum(RETURN_DECISION_STATES),
    decidedBy: z.string(),
    effectiveFrom: z.string(),
    note: z.string().nullable(),
    createdAt: z.string(),
    /**
     * Whether this row is the one the plan pipeline currently reads for its
     * area. Server-computed: it is the newest row whose `effectiveFrom` has
     * passed, and re-deriving that in the browser would be a second
     * implementation of a safety rule.
     */
    inForce: z.boolean(),
});
/**
 * Whether this user's meal plan is withheld, and what became of the last hold.
 *
 * Placed by the coach's safety policy on a message labelled disordered eating;
 * the plan week goes to `safety_hold`, its meals stop being served, its
 * workouts do not, and plan edits answer `DEFERRED`. Ended only from this
 * console — the model that placed it must not be what decides it is over.
 *
 * A user who has never been held reads as `held: false` with a null
 * `placedAt`, rather than as an absent object: the console asked whether this
 * person is held, and "no" is an answer.
 */
export const planSafetyHoldSchema = z.object({
    held: z.boolean(),
    /** Null for a user who has never been held, which is almost everyone. */
    placedAt: z.string().nullable(),
    liftedAt: z.string().nullable(),
    /** The console email of the admin who ended it. */
    liftedBy: z.string().nullable(),
});
/**
 * The subscription behind the header's entitlement badge, and the state the
 * grant panel edits.
 *
 * A user who has never held a subscription reads as `entitlement: 'free'` with
 * null store and status rather than as an absent object — same convention as
 * `planSafetyHoldSchema`. `store: 'promotional'` marks a console grant;
 * `expiresAt` is its optional end (null means until revoked). The store
 * subscription screens read `/admin/subscriptions`; this is the per-user view.
 */
export const userEntitlementSchema = z.object({
    entitlement: z.enum(['free', 'premium']),
    store: z.enum(SUBSCRIPTION_STORES).nullable(),
    status: z.enum(SUBSCRIPTION_STATUSES).nullable(),
    /** `currentPeriodEnd` on the row. Null for a permanent grant — and for no row. */
    expiresAt: z.string().nullable(),
    willRenew: z.boolean(),
    updatedAt: z.string().nullable(),
});
/** What the console sends to grant Premium. Empty body means until revoked. */
export const grantEntitlementSchema = z.object({
    /** Must be in the future; the sweep demotes once it passes. */
    expiresAt: z.string().datetime().optional(),
});
/** What the console sends to record one. `decidedBy` is not here — it is the
 *  acting admin, taken from the session, never typed. */
export const recordReturnDecisionSchema = z.object({
    injuryArea: z.enum(INJURY_AREAS),
    decision: z.enum(RETURN_DECISION_STATES),
    /** Defaults to now. A future date records "cleared from Monday" on Friday
     *  without it taking effect early. */
    effectiveFrom: z.string().datetime().optional(),
    note: z.string().trim().min(1).max(280).optional(),
});
/**
 * The whole per-serving label, because the whole label is subtracted from the
 * user's daily budget. Reading a stack of "24 g protein" told an operator
 * nothing about why a mass gainer user's plan is 800 kcal.
 */
export const userSupplementSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    kcalPerServing: z.number().int(),
    proteinPerServingG: z.number().int(),
    carbsPerServingG: z.number().int(),
    fatPerServingG: z.number().int(),
    servingsPerDay: z.number().int(),
    doseAmount: z.number(),
    doseUnit: z.enum(DOSE_UNITS),
    timing: z.array(z.enum(SUPPLEMENT_TIMINGS)),
});
// --- Gym coaching ------------------------------------------------------------
/** One gym membership of the user, newest first. Facts about the coaching, never its content. */
export const userGymMembershipSchema = z.object({
    membershipId: z.string().uuid(),
    gym: z.object({ id: z.string().uuid(), name: z.string() }),
    status: z.enum(GYM_MEMBER_STATUSES),
    /** Everyone assigned to coach the member, oldest first; empty when none is. */
    trainers: z.array(z.object({
        staffId: z.string().uuid(),
        userId: z.string().uuid().nullable(),
        displayName: z.string(),
        status: z.enum(GYM_STAFF_STATUSES),
    })),
    joinedOn: z.string(),
    /** The day the first plan was published; null until then. */
    firstPlanOn: z.string().nullable(),
    sharing: z.boolean(),
    endedAt: z.string().nullable(),
    endedBy: z.enum(GYM_COACHING_ENDED_BY).nullable(),
});
/** The user as a trainer at a gym. */
export const userGymTrainerRoleSchema = z.object({
    staffId: z.string().uuid(),
    gym: z.object({ id: z.string().uuid(), name: z.string() }),
    status: z.enum(GYM_STAFF_STATUSES),
    acceptedAt: z.string().nullable(),
    removedAt: z.string().nullable(),
    /** Active members assigned to them now. */
    activeMembers: z.number().int(),
});
/** How many of each the detail carries, newest first. */
export const USER_GYM_ROLE_LIMIT = 20;
/**
 * The detail response.
 *
 * Adherence and last-active live here rather than on the list: both are
 * per-user aggregates, affordable for one user and not for a page of them
 * (`users.ts` explains why they are absent there).
 */
export const adminUserDetailSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
    phone: z.string(),
    status: z.enum(ADMIN_USER_STATUSES),
    entitlement: z.enum(['free', 'premium']),
    timezone: z.string(),
    locale: z.string(),
    onboardedAt: z.string().nullable(),
    createdAt: z.string(),
    deactivatedAt: z.string().nullable(),
    profile: userProfileSchema.nullable(),
    goals: z.array(z.enum(GOAL_TYPES)),
    diet: userDietSchema.nullable(),
    workout: userWorkoutPrefsSchema.nullable(),
    /**
     * Newest first. Not gated behind the health reveal: an injury *flag* is
     * already on this response because the exercise filter is built on it, and a
     * decision about one is an operational record rather than a medical fact —
     * the medical detail lives in `WorkoutPreference.injuries`, which is
     * deliberately absent here for exactly that reason.
     */
    returnDecisions: z.array(returnDecisionSchema),
    /** Always present. See `planSafetyHoldSchema`. */
    planSafetyHold: planSafetyHoldSchema,
    /** Always present. See `userEntitlementSchema`. */
    subscription: userEntitlementSchema,
    supplements: z.array(userSupplementSchema),
    targets: nutritionTargetsSchema.nullable(),
    /**
     * What the meal plan is actually built to: `targets` minus the supplement
     * stack. Shown beside `targets` because they diverge — for a mass gainer
     * user they diverge by most of a meal — and an operator reading only
     * `targets` would think the plan was wrong.
     */
    foodBudget: nutritionTargetsSchema.nullable(),
    /** Counts only. The values need `GET /admin/users/:id/health?reveal=true`. */
    health: healthRedactionSchema,
    planStatus: z.enum(PLAN_STATUSES).nullable(),
    /** Latest of any activity we record; null for an account that never did anything. */
    lastActiveAt: z.string().nullable(),
    adherence: adherenceSchema,
    /** Steado for Gyms: where the user is coached, and where they coach. Empty lists for most users. */
    gyms: z.object({
        memberships: z.array(userGymMembershipSchema),
        trainerRoles: z.array(userGymTrainerRoleSchema),
    }),
});
// --- The gated reveal -------------------------------------------------------
/**
 * The only shape carrying health values, returned by the only endpoint that
 * requires role `admin` and writes an audit row before answering.
 *
 * `?reveal=true` is required and must be exactly that. A reveal is a decision,
 * and a decision should be visible in an access log without reading a body.
 */
export const adminUserHealthQuerySchema = z.object({
    reveal: z.literal('true'),
});
export const adminUserHealthSchema = z.object({
    recorded: z.boolean(),
    conditions: z.array(z.enum(HEALTH_CONDITIONS)),
    allergies: z.array(z.enum(ALLERGENS)),
    /** Free text as the user typed it, e.g. "Metformin, Thyronorm 50mcg". */
    medications: z.string().nullable(),
    tracksCycle: z.boolean(),
    pregnantOrNursing: z.boolean(),
    /** Echoed back so the caller can show *what* was just audited against them. */
    revealedAt: z.string(),
});
// --- Plan tab ---------------------------------------------------------------
/**
 * One catalog food on a planned plate.
 *
 * This replaced a `dishes: string[]` of model-authored prose. The console shows
 * the same column, but every row behind it is now a real food with a quantity
 * the server solved for — which is the difference between displaying a plan and
 * displaying a claim.
 */
export const plannedMealItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    portions: z.number(),
    portionLabel: z.string().nullable(),
    grams: z.number(),
});
export const plannedMealSchema = z.object({
    id: z.string().uuid(),
    mealType: z.enum(MEAL_TYPES),
    timeLocal: z.string(),
    /**
     * Orthogonal to `mealType` — a breakfast before an early session is both
     * `breakfast` and `post_workout`. Server-computed from the workout window and
     * the meal's own time, never model-authored.
     */
    trainingRelation: z.enum(TRAINING_RELATIONS),
    /**
     * What the user said about this meal, or null if they have not said.
     *
     * The one thing on the plan tab that is not a prescription — everything else
     * here is what was asked for, this is what happened. Worth showing beside it
     * for exactly that reason: a support conversation about "the plan is not
     * working" is a different conversation when the week is untouched.
     */
    adherence: z
        .object({
        state: z.enum(MEAL_ADHERENCE_STATES),
        /** How much of it, for `partial` only. Strictly between 0 and 1. */
        fraction: z.number().nullable(),
    })
        .nullable(),
    items: z.array(plannedMealItemSchema),
    kcal: z.number().int(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
    note: z.string().nullable(),
});
/**
 * What kind of thing a planned item is, and therefore which of its fields
 * carry a value. A `resistance` item has sets, reps and rest; a `cardio` or
 * `mobility` one has a duration, and a held movement (a plank) done in rounds
 * is a `mobility` item that also has sets and rest: rounds of that one hold.
 * The admin console reads the columns as stored; unlike the app's wire, it has
 * no old build to shield from them. The pairing is a `CHECK` constraint on the
 * table, not a convention — the old shape had every column NOT NULL, so a
 * brisk walk was stored as four sets of eight.
 */
export const PLANNED_ITEM_KINDS = ['resistance', 'cardio', 'mobility'];
export const plannedItemSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(PLANNED_ITEM_KINDS),
    name: z.string(),
    sets: z.number().int().nullable(),
    repsLow: z.number().int().nullable(),
    repsHigh: z.number().int().nullable(),
    restSeconds: z.number().int().nullable(),
    /** Reps in reserve. Null on a timed item, and on a lift nobody prescribed
     *  an effort for. */
    targetRir: z.number().int().nullable(),
    durationSeconds: z.number().int().nullable(),
    note: z.string().nullable(),
});
export const planDaySchema = z.object({
    id: z.string().uuid(),
    dayOfWeek: z.enum(WEEKDAYS),
    /** Null when the week carries no workout row for this day at all. */
    workout: z
        .object({
        title: z.string(),
        isRestDay: z.boolean(),
        items: z.array(plannedItemSchema),
    })
        .nullable(),
    meals: z.array(plannedMealSchema),
});
export const planWeekSummarySchema = z.object({
    id: z.string().uuid(),
    weekStartDate: z.string(),
    status: z.enum(PLAN_STATUSES),
    rationale: z.string().nullable(),
    failureReason: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * The most recent week expanded, the rest as history.
 *
 * There is deliberately no per-week cost, model or token count: nothing records
 * them. `PlanGenerationRun` is Phase 9, and the design's "regenerate" control
 * is Phase 9 too — this tab reads.
 */
/**
 * What the program asked for in the week the current plan covers, read back
 * off the frozen `TrainingWeek.prescription`.
 *
 * Read-only, and read-only for a structural reason rather than a scheduling
 * one: the row is frozen by a database trigger, so an editor here would be a
 * control that cannot work. It is beside the plan because that is the
 * comparison it exists for — the program prescribed this, the plan may have
 * drifted from it.
 *
 * `movementPatterns` is a plain string array rather than the `MOVEMENT_PATTERNS`
 * enum only because `exercises.ts` imports from this file, and the reverse
 * import would be a cycle. The vocabulary is the same one, and drift in it is
 * caught by that file's own pair.
 */
export const trainingWeekSchema = z.object({
    index: z.number().int(),
    phase: z.enum(TRAINING_PHASES),
    sessionsPerWeek: z.number().int(),
    setsPerMovement: z.number().int(),
    repRange: z.object({ low: z.number().int(), high: z.number().int() }),
    targetRir: z.number().int(),
    movementPatterns: z.array(z.string()),
});
export const trainingProgramSchema = z.object({
    name: z.string(),
    startDate: z.string(),
    weekCount: z.number().int(),
    /** Null when the current plan week is not linked to a program week. */
    currentWeek: trainingWeekSchema.nullable(),
});
/**
 * The user's current (or, once it has stopped, most recent) Premium meal plan.
 *
 * A `MEAL_PLAN` row reuses the plan week table, so `status` is the same
 * vocabulary a week has. What differs is the shape of the thing: it runs 1-7
 * days from `weekStartDate` rather than seven from a Monday, and it can be
 * ended early by the person who asked for it.
 *
 * `endDate` is derived (`weekStartDate + dayCount - 1`) rather than read off a
 * column, because there is no column: a plan whose last day has passed can sit
 * with `endedAt` null for days, until something reads it. The two facts are
 * both here on purpose — `endDate` is when it stopped covering days, `endedAt`
 * is when that was recorded.
 */
export const mealPlanSummarySchema = z.object({
    id: z.string().uuid(),
    /** Its first day, not a Monday. */
    weekStartDate: z.string(),
    /** 1-7. */
    dayCount: z.number().int().min(1).max(7),
    /** Its last day, derived from the two above. */
    endDate: z.string(),
    status: z.enum(PLAN_STATUSES),
    /** When the plan was recorded as stopped. Null while it runs. */
    endedAt: z.string().nullable(),
    endedBy: z.enum(MEAL_PLAN_ENDED_BY).nullable(),
});
/**
 * Why the weekly sweep will not renew this user's workout plan.
 *
 * Mirrors the skip reasons in `weekly-replan.service.ts`, so the console says
 * what the cron will actually do rather than a console-only paraphrase of it.
 * `free` is the one that is a product decision rather than a state: a plan is
 * something a subscriber has, and a free user's week is left where it is.
 */
export const WORKOUT_RENEWAL_REASONS = [
    'gym_coached',
    'nothing_to_plan',
    'dormant',
    'lapsed',
    'not_due',
    'asked_today',
];
/**
 * Why the weekly sweep will not renew this user's meal plan.
 *
 * `never_made` is the opt-in and is the common answer: a meal plan is
 * something the user asks for, and an account that has never had one is never
 * given one. `not_due` means the plan it holds is still running.
 */
export const MEAL_RENEWAL_REASONS = [
    'gym_coached',
    'not_premium',
    'never_made',
    'in_flight',
    'failed',
    'ended_by_user',
    'dormant',
    'not_due',
    'lapsed',
];
/**
 * Whether each track renews by itself, and when it does not, why.
 *
 * Two tracks because they are two plans with two cycles: someone can start
 * meals on Monday and workouts on Wednesday, and each falls due on its own
 * date. `reason` is null exactly when `renews` is true.
 */
export const planRenewalSchema = z.object({
    workouts: z.object({
        renews: z.boolean(),
        reason: z.enum(WORKOUT_RENEWAL_REASONS).nullable(),
    }),
    meals: z.object({
        renews: z.boolean(),
        reason: z.enum(MEAL_RENEWAL_REASONS).nullable(),
    }),
});
export const adminUserPlanSchema = z.object({
    current: planWeekSummarySchema.extend({ days: z.array(planDaySchema) }).nullable(),
    history: z.array(planWeekSummarySchema),
    /** Null for a user with no program — one is derived at the first activation
     *  from the workout section, so a user who never answered it has none. */
    program: trainingProgramSchema.nullable(),
    /**
     * The Premium meal plan beside the weekly plan, or null for someone who has
     * never made one. Separate from `current`, which is always the weekly plan:
     * the two are different rows with different lifetimes, and collapsing them
     * into one "current plan" would make a user who holds both unreadable.
     */
    mealPlan: mealPlanSummarySchema.nullable(),
    /**
     * The pair that explains why this user does or does not get plans, read
     * together.
     *
     * Neither answers it alone. `entitlement` decides whether anything is
     * generated at all (a free user's week is carried forward, and they get no
     * meal plan); `mealPlanMode` decides whether the week that is generated
     * carries meals. Showing one without the other is how an operator concludes
     * "Premium, so they should have meals".
     */
    mealPlanMode: z.enum(MEAL_PLAN_MODES),
    entitlement: z.enum(['free', 'premium']),
    renewal: planRenewalSchema,
});
// --- Logs tab ---------------------------------------------------------------
export const mealLogSchema = z.object({
    id: z.string().uuid(),
    mealType: z.enum(MEAL_TYPES),
    source: z.enum(MEAL_LOG_SOURCES),
    loggedAt: z.string(),
    localDate: z.string(),
    /** Item names only — the tab is a timeline, not a nutrition editor. */
    items: z.array(z.string()),
    kcal: z.number().int(),
    proteinG: z.number(),
});
export const workoutSessionSchema = z.object({
    id: z.string().uuid(),
    title: z.string().nullable(),
    status: z.enum(SESSION_STATUSES),
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    localDate: z.string(),
    perceivedExertion: z.number().int().nullable(),
    setCount: z.number().int(),
});
export const weightPointSchema = z.object({
    localDate: z.string(),
    weightKg: z.number(),
    source: z.enum(WEIGHT_SOURCES),
});
export const adminUserLogsSchema = z.object({
    windowDays: z.number().int(),
    /** Inclusive local-date bounds the three lists were read over. */
    from: z.string(),
    to: z.string(),
    meals: z.array(mealLogSchema),
    workouts: z.array(workoutSessionSchema),
    /** Oldest first, so a chart can plot it without sorting. */
    weights: z.array(weightPointSchema),
});
// --- Coach tab --------------------------------------------------------------
export const chatSessionSummarySchema = z.object({
    id: z.string().uuid(),
    status: z.enum(CHAT_SESSION_STATUSES),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    messageCount: z.number().int(),
});
export const userMemorySchema = z.object({
    id: z.string().uuid(),
    content: z.string(),
    source: z.string(),
    createdAt: z.string(),
    /** Set when a later fact contradicted this one; the row is kept, not deleted. */
    supersededAt: z.string().nullable(),
});
export const adminUserCoachSchema = z.object({
    sessions: z.array(chatSessionSummarySchema),
    memories: z.array(userMemorySchema),
});
/**
 * A tool call as the row stores it. `arguments` stays an opaque JSON value: the
 * shape differs per tool and pretending otherwise in a shared schema would mean
 * inventing a union that the column does not enforce.
 */
export const chatToolCallSchema = z.object({
    id: z.string().nullable(),
    name: z.string(),
    arguments: z.unknown(),
});
export const chatMessageSchema = z.object({
    id: z.string().uuid(),
    role: z.enum(CHAT_ROLES),
    content: z.string(),
    toolName: z.string().nullable(),
    toolCalls: z.array(chatToolCallSchema),
    createdAt: z.string(),
});
export const adminUserTranscriptSchema = z.object({
    session: chatSessionSummarySchema,
    messages: z.array(chatMessageSchema),
});
// --- Scans tab --------------------------------------------------------------
/** One dish on the plate. A scan holds several — a thali is one photo, many dishes. */
export const foodScanItemSchema = z.object({
    id: z.string().uuid(),
    dishName: z.string(),
    estimatedPortionGrams: z.number(),
    /** The catalog portion the count was priced from ("1 katori"), when there was one. */
    portionLabel: z.string().nullable(),
    /** The vision model's own confidence in the identification, 0–1. */
    confidence: z.number(),
    /** "catalog" (T1) or "decomposition" (T3); a plain string, as the column is. */
    matchTier: z.string().nullable(),
    matchedFoodName: z.string().nullable(),
    /** T3 only, 0–1: how much of the dish's weight resolved to the catalog. */
    ingredientCoverage: z.number().nullable(),
    unresolvedIngredients: z.array(z.string()),
    /** Why this one dish could not be priced while its neighbours could. */
    failureReason: z.string().nullable(),
});
export const foodScanSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(FOOD_SCAN_STATUSES),
    items: z.array(foodScanItemSchema),
    /** Set only when the whole scan failed; a single bad dish fails on its item. */
    failureCode: z.enum(FOOD_SCAN_FAILURE_CODES).nullable(),
    createdAt: z.string(),
    /** Whether the proposal was acted on. The signal the scan pipeline is judged by. */
    loggedAsMeal: z.boolean(),
});
/**
 * The scan list is the one tab that genuinely grows without bound for an active
 * user, so it pages rather than taking a window.
 */
export const adminUserScansQuerySchema = cursorPageQuerySchema;
// --- Shared path parameters -------------------------------------------------
export const adminUserParamsSchema = z.object({
    id: z.string().uuid(),
});
export const adminUserSessionParamsSchema = z.object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
});
/** The tabs the detail screen offers, in the order the design shows them. */
export const USER_DETAIL_TABS = ['profile', 'plan', 'logs', 'coach', 'scans', 'ai', 'rhythm'];
//# sourceMappingURL=user-detail.js.map